/**
 * Community → Games API (published games only) + signed play sessions,
 * server-verified completions and Share to Community.
 */
import api from "../../Environment";

function unwrap(response) {
  return response && response.data ? response.data : response;
}

function statusOf(error) {
  return error && error.response ? error.response.status : 0;
}

export async function fetchGames() {
  try {
    const body = unwrap(await api.postMethod("games", {}));
    if (body && body.success && body.data && Array.isArray(body.data.games)) {
      return { games: body.data.games, error: false };
    }
    return { games: [], error: true };
  } catch (e) {
    return { games: [], error: true };
  }
}

export async function fetchGame(slug) {
  try {
    const body = unwrap(await api.postMethod("games/show", { slug: slug || "" }));
    if (body && body.success && body.data && body.data.game) {
      return { game: body.data.game, error: false, notFound: false };
    }
    return { game: null, error: true, notFound: false };
  } catch (e) {
    const notFound = statusOf(e) === 404;
    return { game: null, error: !notFound, notFound };
  }
}

async function playCall(action, payload, fallback) {
  try {
    const body = unwrap(await api.postMethod(action, payload));
    if (body && body.success && body.data) {
      return { ok: true, data: body.data, error: null };
    }
    return { ok: false, data: null, error: (body && body.error) || fallback };
  } catch (e) {
    const body = e && e.response ? unwrap(e.response) : null;
    return { ok: false, data: null, error: (body && body.error) || fallback };
  }
}

/** Signed play session for one level (needed to share a completion). */
export function startPlay(slug, levelId) {
  return playCall("games/play/start", { slug: slug || "", level_id: levelId }, "Sharing is unavailable right now.");
}

/** Trivia: record one answer → { correct, correct_option_id, answered, correct_count, total }. */
export function answerQuestion(token, questionId, optionId) {
  return playCall(
    "games/play/answer",
    { play_token: token || "", question_id: questionId || "", option_id: optionId || "" },
    "We couldn’t check your answer. Please try again."
  );
}

/** Crossword: per-entry status of the letters → { entries: [{ number, direction, status }], complete, filled, total }. */
export function checkCrossword(token, rows) {
  return playCall(
    "games/play/check",
    { play_token: token || "", letters: JSON.stringify(Array.isArray(rows) ? rows : []) },
    "We couldn’t check your answers. Please try again."
  );
}

/** Server-verified completion → { completion: { id, headline, link, ... } | null, result }. */
export function completePlay(token, proof) {
  return playCall(
    "games/play/complete",
    { play_token: token || "", proof: JSON.stringify(proof || {}) },
    "We couldn’t verify this completion. Please try again."
  );
}

/** Create the Community post once per completion (retries return the same post). */
export function shareCompletion(completionId, caption, includeScore) {
  return playCall(
    "games/play/share",
    { completion_id: completionId, caption: caption || "", include_score: includeScore ? "1" : "0" },
    "Couldn’t share right now. Please try again."
  );
}
